import { isAxiosError } from 'axios';
import { WalletContractError } from './services/Wallet.service';

/**
 * Pure decision surface for the bounded balance-settlement wait after an
 * accepted top-up (RET-1870, KTD7).
 *
 * No React, no timers, no network. This module only answers three questions:
 * how long to wait between retries, whether a caught failure should stop the
 * wait or be absorbed, and whether the balance actually went up. The wait
 * itself — timers, cancellation, generations — lives in `Wallet.provider.tsx`
 * (U3); it reads these exports instead of restating them, so tuning the
 * schedule later is a number changed here, not a redesign there.
 *
 * Sibling pattern: like `Wallet.money.ts` and `Wallet.attempt.ts`, this is a
 * standalone pure module with its own `.test.ts` next to it.
 */

/**
 * Waits between settlement retries, in milliseconds, increasing.
 *
 * The FIRST query after a charge is accepted fires immediately (tick 0, not
 * represented here) because the customer is looking at the screen at that
 * instant. This array is the wait BEFORE each retry that follows tick 0, so
 * with 4 entries the wait covers 4 retries plus the immediate first read.
 *
 * The sum equals `SETTLEMENT_BUDGET_MS` on purpose: the schedule and the
 * budget are the same fact, kept as two separate exports (instead of
 * deriving the budget from the array, or the array from the budget) so a
 * reader sees the total without adding, and so a test can assert they still
 * agree after either one changes.
 *
 * Doubling on each step, four steps, 15s total — this ladder is an unmeasured
 * guess (see the plan's "the 15-second budget is an unmeasured bet": nobody
 * has measured the real latency between the 200 and the webhook credit in
 * production, because the webhook has never run live). Exporting it as data
 * instead of hardcoding a loop inside the provider is what makes correcting
 * that guess later a one-line change instead of a redesign.
 */
export const SETTLEMENT_WAIT_SCHEDULE_MS = [1000, 2000, 4000, 8000] as const;

/** Total time budget for the settlement wait, in milliseconds. */
export const SETTLEMENT_BUDGET_MS = 15_000;

/**
 * Whether a failed balance query should stop the settlement wait right away
 * ('terminal') or be absorbed while the wait keeps going ('transient').
 */
export type tSettlementFailureClass = 'terminal' | 'transient';

/**
 * HTTP statuses that stop the wait immediately (KTD4). None of these are fixed
 * by retrying within the 15s budget, and each one below was checked against
 * what the BFF actually answers, not against what the code that reads it says:
 *
 * - **400** — `wid` absent or not a UUID (front contract §3.1). It cannot
 *   change while the wait runs.
 * - **401** — invalid or absent Firebase token. `getBalance` opts out of the
 *   shared interceptor (`skipAutoRedirect`), so nothing redirects and nothing
 *   re-authenticates: stopping does not recover the session — only a reload or
 *   a navigation does — it stops spending reads on one that is already gone.
 * - **403** — not a member of this workspace. Terminal either way, and the
 *   reasoning does not depend on the deploy: a 403 that arrives is a real
 *   denial, and one that cannot arrive costs nothing to classify. RET-1843 puts
 *   every wallet BFF route behind `workspace.RequireWorkspaceMembership`, which
 *   propagates a transport failure's own status instead of masking it as 403 —
 *   so once that lands, a 403 here is never a flake either. It is In Review /
 *   Staging as of 2026-08-25, NOT in production; do not read this line as a
 *   guarantee that already holds in prod.
 * - **422** — wallet-service answered 2xx with a body missing `amount` /
 *   `amountMicros`, converted by the BFF (front contract §3.1). A sibling of
 *   `WalletContractError`, not a twin that makes it unreachable: 422 is the BFF
 *   rejecting wallet-service's body, `WalletContractError` is this repo
 *   rejecting the BFF's.
 *
 * 503 is deliberately absent — see the classifier below.
 */
const TERMINAL_HTTP_STATUSES: ReadonlySet<number> = new Set([
  400, 401, 403, 422,
]);

/**
 * Classifies a caught balance-query failure as terminal or transient.
 *
 * `WalletContractError` is thrown in-process by `WalletService.getBalance`
 * itself, not by axios, and it is always terminal: it is the code-side twin
 * of the wire 422, a response that parsed but does not match the money
 * contract.
 *
 * An axios error WITH an HTTP response is terminal only for the codes in
 * `TERMINAL_HTTP_STATUSES`. Every other status — including 424
 * (wallet-service unreachable or 5xx, per the contract) — is transient: the
 * request may simply succeed on the next tick.
 *
 * 503 among them, which contradicts the contract only in appearance. The
 * contract's 503 is `WALLET_SERVICE_URL` / `WALLET_INTERNAL_API_KEY` unset —
 * "the wallet is not deployed here" — and never recovers. It also never reaches
 * this function: the same missing configuration fails `getTopUpOptions`,
 * `ConnectedTopUpModal` returns null, no charge can be submitted and no wait
 * can start. The only 503 that CAN arrive here is a load balancer during a
 * rollout, over in seconds. Retrying costs at most four reads; cutting the wait
 * costs a customer who already paid the sight of their own credit.
 *
 * An axios error with NO response covers both a network failure and a
 * timeout: axios reports both the same way, without a `response` object (see
 * `isAxiosTimeoutError` in `sentry.singleton.ts`), so neither needs its own
 * branch here. Both are transient — the request never reached a server that
 * could give a terminal answer.
 *
 * Anything that is not a `WalletContractError` and not an axios error at all
 * falls through to transient too. An unrecognized failure shape must not
 * bias toward cutting the wait short on a customer who already paid: failing
 * open means failing toward MORE waiting, never less.
 */
export function classifySettlementFailure(
  error: unknown,
): tSettlementFailureClass {
  if (error instanceof WalletContractError) return 'terminal';

  if (isAxiosError(error) && error.response) {
    return TERMINAL_HTTP_STATUSES.has(error.response.status)
      ? 'terminal'
      : 'transient';
  }

  return 'transient';
}

/**
 * Whether the balance went UP from the baseline, comparing two `amountMicros`
 * strings BY VALUE, never by string equality and never through `Number()`.
 *
 * An INCREASE, not any change. This wallet is debited continuously by outbound
 * WhatsApp template sends, so a debit landing inside the 15s window is ordinary
 * traffic: a predicate that stopped on any inequality ended the wait on that
 * debit and left the customer on a balance LOWER than before their top-up —
 * they recharge and watch the number go down. Rejected: stopping on any change
 * and letting a later read correct it. There is no later read — stopping IS the
 * end of the wait, and nothing else polls the balance.
 *
 * `BigInt`, because a backend formatting change alone — `"5000000"` vs
 * `"05000000"` — must read as NO increase. A string comparison would see that
 * pair as a change and stop the wait on a phantom credit that never
 * happened. `Wallet.types.ts` bans float arithmetic on money in the front for
 * the same reason `Wallet.money.ts` does (drift, values past 2^53); this
 * function stays inside that rule by never returning or exposing a number —
 * only a boolean.
 *
 * A value that is not a valid integer string reads as NO increase instead of
 * throwing: a settlement wait that crashes on a malformed read is worse than
 * one that just keeps waiting for the next tick.
 */
export function hasSettlementBalanceIncreased(
  baselineAmountMicros: string,
  currentAmountMicros: string,
): boolean {
  const baseline = parseIntegerMicros(baselineAmountMicros);
  const current = parseIntegerMicros(currentAmountMicros);

  if (baseline === null || current === null) return false;

  return current > baseline;
}

function parseIntegerMicros(value: string): bigint | null {
  if (!/^-?\d+$/.test(value)) return null;

  return BigInt(value);
}
